/**
 * Twelve-month balance sparkline for a fund card.
 *
 * Plots HISTORICAL cost, not today's valuation, so the line shows money
 * actually flowing in and out rather than jumping every time the lira moves.
 * The headline figure beside it is current value — that difference is
 * deliberate and is what the unrealised FX number explains.
 */
import { useId } from 'react';
import { cn } from '@/lib/utils';
import type { BalancePoint } from '@/lib/types';

export function Sparkline({
  points,
  className,
  height = 40,
}: {
  points: BalancePoint[];
  className?: string;
  height?: number;
}): JSX.Element | null {
  const gradientId = useId();

  if (points.length < 2) return null;

  const values = points.map((p) => p.baseMinor);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const span = max - min || 1;

  const width = 100; // viewBox units; the SVG scales to its container
  const step = width / (points.length - 1);

  const coords = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / span) * height;
    return [x, y] as const;
  });

  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;

  const last = values[values.length - 1]!;
  const first = values[0]!;
  const rising = last >= first;
  const stroke = rising ? 'hsl(var(--income))' : 'hsl(var(--expense))';

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn('h-10 w-full', className)}
      role="img"
      aria-label={`Движение за последните ${points.length} месеца, ${rising ? 'нагоре' : 'надолу'}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.25" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
